import { formatUnits } from 'viem'
import { compact } from './format'

/**
 * WAD helpers.
 *
 * The protocol expresses every ratio and every USDT amount as an 18-decimal fixed-point
 * integer — `1.5e18` is an LCR of 1.5×, `1.12e18` a Premium Index of 1.12. Rendering
 * those requires knowing which of the three shapes a given field is, so each shape gets
 * its own function rather than one `formatWad` that callers have to think about.
 *
 * Conversion to `number` happens ONLY here, at the point of display. A WAD value up to
 * ~9,007 million survives a double exactly; beyond that the low digits are lost, which
 * is acceptable for a rendered figure and never for arithmetic. No caller should do
 * maths on the result.
 */

export const WAD = 10n ** 18n

/** WAD → float, for display only. */
export function fromWad(value: bigint): number {
  return Number(formatUnits(value, 18))
}

/** A USDT amount: `$1.24M`, `$18.3K`, `$942.05`. */
export function formatWadUsd(value: bigint): string {
  return `$${compact(fromWad(value))}`
}

/** A USDT amount at full precision, for a figure someone may need to reconcile. */
export function formatWadExact(value: bigint, maxFractionDigits = 2): string {
  return fromWad(value).toLocaleString(undefined, { maximumFractionDigits: maxFractionDigits })
}

/** A ratio rendered as a multiple: `1.52×`. Used for LCR and the Premium Index. */
export function formatWadMultiple(value: bigint, digits = 2): string {
  return `${fromWad(value).toFixed(digits)}×`
}

/** A WAD fraction as a percentage: `0.184e18` → `18.4%`. */
export function formatWadPercent(value: bigint, digits = 1): string {
  return `${(fromWad(value) * 100).toFixed(digits)}%`
}

/**
 * A per-token price. Precision scales with magnitude, because a fixed 2 decimals
 * renders a sub-cent token as `$0.00` and every move as no move at all.
 */
export function formatWadPrice(value: bigint): string {
  const n = fromWad(value)
  if (!Number.isFinite(n)) return '—'
  if (n >= 1000) return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
  if (n >= 1) return `$${n.toFixed(4)}`
  if (n >= 0.01) return `$${n.toFixed(5)}`
  if (n > 0) return `$${n.toPrecision(4)}`
  return '$0'
}

/**
 * A WAD fraction of the whole, clamped to 0–100 for use as a bar width.
 *
 * Clamped because these come from live ratios that can legitimately exceed 1 — an LCR
 * of 3.2× is healthy, not a bar three times too wide.
 */
export function wadToPercentWidth(value: bigint, fullScale: bigint = WAD): number {
  if (fullScale === 0n) return 0
  return Math.max(0, Math.min(100, (fromWad(value) / fromWad(fullScale)) * 100))
}
